import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  Switch,
  Textarea,
} from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IntegrationListItem, useUpdateIntegration } from '@/entities/integrations';

type EditIntegrationDialogProps = {
  integration: IntegrationListItem | null;
  isOpen: boolean;
  onClose: () => void;
};

type EditIntegrationFormValues = {
  name: string;
  description: string;
  isActive: boolean;
};

export const EditIntegrationDialog: FC<EditIntegrationDialogProps> = ({ integration, isOpen, onClose }) => {
  const { t } = useTranslation(['common', 'integrations']);
  const updateIntegration = useUpdateIntegration();

  const methods = useForm<EditIntegrationFormValues>({
    defaultValues: {
      name: integration?.name ?? '',
      description: integration?.description ?? '',
      isActive: integration?.isActive ?? false,
    },
  });
  const { control, handleSubmit, reset, formState } = methods;

  // Подтягиваем поля из выбранной интеграции при открытии диалога
  useEffect(() => {
    if (isOpen && integration) {
      reset({
        name: integration.name,
        description: integration.description ?? '',
        isActive: integration.isActive,
      });
    }
  }, [isOpen, integration, reset]);

  const handleClose = () => {
    if (updateIntegration.isPending) {
      return;
    }
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const submit = handleSubmit(async (values) => {
    if (!integration) {
      return;
    }
    await updateIntegration.mutateAsync({
      code: integration.code,
      payload: {
        name: values.name.trim(),
        description: values.description.trim() || null,
        isActive: values.isActive,
      },
    });
    onClose();
  });

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('integrations:dialog.title')}</DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <form className="flex flex-col gap-4 py-2" onSubmit={submit}>
            <Input label={t('integrations:dialog.fields.code')} value={integration?.code ?? ''} disabled />
            <Input
              control={control}
              name="name"
              label={t('integrations:dialog.fields.name')}
              rules={{
                required: { value: true, message: t('common:required') },
              }}
            />
            <Switch control={control} name="isActive" label={t('integrations:dialog.fields.isActive')} />
            <Textarea
              control={control}
              name="description"
              label={t('integrations:dialog.fields.description')}
              rows={4}
            />
          </form>
        </FormProvider>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={updateIntegration.isPending}>
            {t('integrations:dialog.buttons.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={updateIntegration.isPending || (!formState.isValid && formState.isSubmitted)}
          >
            {t('integrations:dialog.buttons.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
